/**
 * Carrinho lateral no padrão da tela "Seus produtos" do quiz (ver snippets/narte-cart-escada.liquid).
 *
 * - Extras do carrossel: "Adicionar", quantidade e "Saiba mais" (pop-up por cima do carrinho).
 * - Tirar um produto que derruba um degrau da escada: o produto sai na hora e um aviso no rodapé diz o
 *   benefício perdido, com "Desfazer" (mesmo comportamento da tela do quiz).
 * - "Finalizar": se faltar pouco (até R$ 60) para o próximo degrau, oferece o extra que completa.
 *
 * O carrinho lateral é redesenhado inteiro pelo tema a cada mudança (morph da seção do cabeçalho).
 * Avisos e pop-ups ficam numa camada dentro do <dialog> marcada para o tema não redesenhar.
 */

const NEAR_LIMIT = 6000;
const NEAR_KEY = 'narte:cart-near-shown';

const state = {
  /** @type {any} */ cart: null,
  lastDrawerTouch: 0,
  busy: false,
  /** @type {ReturnType<typeof setTimeout> | undefined} */ snapTimer: undefined,
  /** @type {ReturnType<typeof setTimeout> | undefined} */ toastTimer: undefined,
};

const money = (cents) =>
  (Number(cents || 0) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const escapeHtml = (text) =>
  String(text ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

const drawerDialog = () => document.querySelector('.cart-drawer__dialog');
const ladderEl = () => /** @type {HTMLElement|null} */ (document.querySelector('.cart-drawer [data-narte-ladder]'));

function tiers() {
  try {
    return JSON.parse(ladderEl()?.dataset.tiers || '[]');
  } catch (e) {
    return [];
  }
}

const isGift = (item) => Boolean(item?.properties && (item.properties._gift || item.properties.gift));

/** Mesma conta da escada no Liquid: total sem brindes + desconto automático devolvido. */
function ladderValue(cart) {
  let value = 0;
  let auto = 0;
  for (const item of cart?.items || []) {
    if (!isGift(item)) value += Number(item.final_line_price || 0);
    for (const allocation of item.line_level_discount_allocations || []) {
      const type = allocation.discount_application?.type;
      if (type !== 'discount_code' && type !== 'manual') auto += Number(allocation.amount || 0);
    }
  }
  for (const application of cart?.cart_level_discount_applications || []) {
    if (application.type !== 'discount_code' && application.type !== 'manual') {
      auto += Number(application.total_allocated_amount || 0);
    }
  }
  return { value: value + auto, auto };
}

function reachedTiers(cart) {
  const { value, auto } = ladderValue(cart);
  return tiers().filter((t) => (t.discount ? auto > 0 && value >= t.amount : value >= t.amount));
}

async function fetchCart() {
  const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
  return response.json();
}

/** Avisa o tema que o carrinho mudou: ele redesenha o carrinho lateral e acerta os brindes. */
function announce() {
  document.dispatchEvent(
    new CustomEvent('cart:update', { bubbles: true, detail: { resource: {}, sourceId: 'narte-cart', data: { source: 'narte-cart' } } })
  );
}

async function post(path, body) {
  if (state.busy) return false;
  state.busy = true;
  drawerDialog()?.querySelector('cart-items-component')?.classList.add('cart-items-disabled');
  try {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(String(response.status));
    announce();
    return true;
  } catch (error) {
    console.error('[narte-cart]', error);
    return false;
  } finally {
    state.busy = false;
    drawerDialog()?.querySelector('cart-items-component')?.classList.remove('cart-items-disabled');
  }
}

/* ------------------------------------------------------------------ */
/* Camada por cima do carrinho                                          */
/* ------------------------------------------------------------------ */

/**
 * Camada dos avisos e pop-ups: fica DENTRO do <dialog> do carrinho (fora dele o Safari trava o toque,
 * porque o resto da página fica inerte com o carrinho aberto) e protegida do redesenho do tema
 * pelo atributo data-skip-subtree-update (assets/morph.js). Vem pronta em snippets/cart-drawer.liquid.
 */
function layer() {
  const dialog = drawerDialog();
  let el = /** @type {HTMLElement|null} */ (dialog?.querySelector('.narte-cart-layer') || null);
  if (!el && dialog) {
    el = document.createElement('div');
    el.className = 'narte-cart-layer';
    el.setAttribute('data-skip-subtree-update', '');
    dialog.appendChild(el);
  }
  return /** @type {HTMLElement} */ (el || document.createElement('div'));
}

const showLayer = () => layer();

function hideLayerIfEmpty() {}

/** "Você perdeu os 10% OFF. Seu desconto caiu para 5% OFF." / "Você perdeu o frete fixo de R$ 9,90." */
function lossLabel(lost, kept) {
  const parts = [];
  const shipping = lost.find((t) => !t.discount);
  if (shipping) parts.push(`o ${shipping.reward || shipping.name}`);
  const discounts = lost.filter((t) => t.discount);
  const top = discounts[discounts.length - 1];
  if (top) parts.push(`os ${top.reward || top.name}`);
  const keptDiscount = kept.filter((t) => t.discount).pop();
  let label = `Você perdeu ${parts.join(' e ')}.`;
  if (top && keptDiscount) label += ` Seu desconto caiu para ${keptDiscount.reward || keptDiscount.name}.`;
  return label;
}

function showToast(lost, kept, undo) {
  const el = showLayer();
  el.querySelector('.narte-cart-layer__toast')?.remove();
  clearTimeout(state.toastTimer);
  const label = lossLabel(lost, kept);
  const toast = document.createElement('div');
  toast.className = 'narte-cart-layer__toast';
  toast.setAttribute('role', 'status');
  toast.innerHTML = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8z"/><circle cx="7.5" cy="7.5" r="1.5"/></svg><span>${escapeHtml(label)}</span><button type="button">Desfazer</button>`;
  const hide = () => {
    clearTimeout(state.toastTimer);
    toast.remove();
    hideLayerIfEmpty();
  };
  toast.querySelector('button')?.addEventListener('click', async (e) => {
    // O tema fecha o carrinho quando o toque "cai fora" dele; com o botão já removido, ele acharia que caiu.
    e.stopPropagation();
    setTimeout(hide, 0);
    await undo();
  });
  el.appendChild(toast);
  state.toastTimer = setTimeout(hide, 7000);
}

/**
 * Pop-up por cima do carrinho. Resolve true no botão principal, false no secundário, null ao fechar.
 * @returns {Promise<boolean|null>}
 */
function openSheet({ html, yes, no }) {
  const el = showLayer();
  el.querySelector('.narte-cart-layer__sheet')?.remove();
  el.querySelector('.narte-cart-layer__toast')?.remove();
  const sheet = document.createElement('div');
  sheet.className = 'narte-cart-layer__sheet';
  sheet.innerHTML = `<div class="narte-cart-layer__scrim"></div>
    <div class="narte-cart-layer__panel" role="dialog" aria-modal="true">
      <button type="button" class="narte-cart-layer__close" aria-label="Fechar">×</button>
      <div class="narte-cart-layer__body">${html}</div>
      <div class="narte-cart-layer__foot">
        ${yes ? `<button type="button" class="narte-cart-layer__cta" data-yes>${escapeHtml(yes)}</button>` : ''}
        ${no ? `<button type="button" class="narte-cart-layer__link" data-no>${escapeHtml(no)}</button>` : ''}
      </div>
    </div>`;
  el.appendChild(sheet);
  sheet.querySelector('[data-yes], .narte-cart-layer__close')?.focus({ preventScroll: true });
  return new Promise((resolve) => {
    const done = (value) => {
      setTimeout(() => sheet.remove(), 0);
      hideLayerIfEmpty();
      resolve(value);
    };
    sheet.addEventListener('click', (e) => {
      // Toques no pop-up não podem chegar ao carrinho: ele fecharia (ver showToast).
      e.stopPropagation();
      const t = /** @type {HTMLElement} */ (e.target);
      if (t.closest('[data-yes]')) done(true);
      else if (t.closest('[data-no]')) done(false);
      else if (t.closest('.narte-cart-layer__close, .narte-cart-layer__scrim')) done(null);
    });
  });
}

/* ------------------------------------------------------------------ */
/* Tirar produto que derruba um degrau → Desfazer                       */
/* ------------------------------------------------------------------ */

async function snapshot() {
  let fresh;
  try {
    fresh = await fetchCart();
  } catch (e) {
    return;
  }
  const previous = state.cart;
  state.cart = fresh;
  if (!previous || Date.now() - state.lastDrawerTouch > 8000) return;

  // Linhas (sem brinde) que diminuíram ou saíram.
  const freshByKey = new Map((fresh.items || []).map((i) => [i.key, i]));
  const decreased = (previous.items || []).filter((item) => {
    if (isGift(item)) return false;
    const now = freshByKey.get(item.key);
    return !now || now.quantity < item.quantity;
  });
  if (!decreased.length) return;
  // Mudança que também aumentou algo (troca de variante etc.): não é "tirar".
  const grew = (fresh.items || []).some((i) => {
    const before = (previous.items || []).find((p) => p.key === i.key);
    return !isGift(i) && (!before || i.quantity > before.quantity);
  });
  if (grew) return;

  const had = reachedTiers(previous);
  const kept = reachedTiers(fresh);
  const has = new Set(kept.map((t) => t.amount));
  const lost = had.filter((t) => !has.has(t.amount));
  if (!lost.length) return;

  const undo = async () => {
    for (const item of decreased) {
      const now = freshByKey.get(item.key);
      if (now) {
        await post('cart/change.js', { id: item.key, quantity: item.quantity });
      } else {
        const properties = { ...(item.properties || {}) };
        await post('cart/add.js', { items: [{ id: item.variant_id, quantity: item.quantity, properties }] });
      }
    }
  };
  showToast(lost, kept, undo);
}

function scheduleSnapshot() {
  clearTimeout(state.snapTimer);
  // Espera o tema e o gerenciador de brindes terminarem (eles disparam várias atualizações seguidas).
  state.snapTimer = setTimeout(snapshot, 900);
}

/* ------------------------------------------------------------------ */
/* Cliques no carrinho lateral                                          */
/* ------------------------------------------------------------------ */

async function onClick(event) {
  const target = /** @type {HTMLElement} */ (event.target);
  if (!target.closest?.('.cart-drawer__dialog')) return;

  const add = target.closest('[data-narte-add]');
  if (add) {
    event.preventDefault();
    await post('cart/add.js', { items: [{ id: Number(add.getAttribute('data-narte-add')), quantity: 1 }] });
    return;
  }

  const qty = target.closest('[data-narte-qty]');
  if (qty) {
    event.preventDefault();
    await post('cart/change.js', { id: qty.getAttribute('data-key'), quantity: Number(qty.getAttribute('data-narte-qty')) });
    return;
  }

  const more = target.closest('[data-narte-more]');
  if (more) {
    event.preventDefault();
    const card = /** @type {HTMLElement|null} */ (more.closest('[data-narte-extra]'));
    if (!card) return;
    const details = /** @type {HTMLTemplateElement|null} */ (card.querySelector('template[data-narte-details]'));
    const image = card.dataset.image ? `<div class="narte-cart-layer__media"><img src="${escapeHtml(card.dataset.image)}" alt=""></div>` : '';
    const inCart = Boolean(card.querySelector('[data-narte-qty]'));
    const choice = await openSheet({
      html: `${image}<h2 class="narte-cart-layer__title" style="margin-top: 0.9rem">${escapeHtml(card.dataset.title)}</h2>
        <p class="narte-cart-layer__text" style="color: var(--narte-ink); font-weight: 600">${money(card.dataset.price)}</p>
        ${details ? details.innerHTML : ''}`,
      yes: inCart ? 'Voltar ao carrinho' : 'Adicionar ao carrinho',
    });
    if (choice && !inCart) await post('cart/add.js', { items: [{ id: Number(card.dataset.narteExtra), quantity: 1 }] });
  }
}

/** "Finalizar": se faltar pouco para o próximo degrau, oferece o extra que completa (uma vez por visita). */
async function onCheckout(event) {
  const target = /** @type {HTMLElement} */ (event.target);
  const trigger = /** @type {HTMLElement|null} */ (target.closest?.('.cart-drawer__dialog [name="checkout"]'));
  if (!trigger || trigger.dataset.narteNear === 'done') return;

  const ladder = ladderEl();
  const next = Number(ladder?.dataset.nextAmount || 0);
  const missing = next - Number(ladder?.dataset.ladderValue || 0);
  const highlight = /** @type {HTMLElement|null} */ (document.querySelector('.cart-drawer [data-narte-extra].is-highlight'));
  let shown = false;
  try {
    shown = sessionStorage.getItem(NEAR_KEY) === '1';
  } catch (e) {}
  if (!(next > 0 && missing > 0 && missing <= NEAR_LIMIT && highlight) || shown) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  try {
    sessionStorage.setItem(NEAR_KEY, '1');
  } catch (e) {}

  const prize = ladder?.dataset.nextReward || 'o próximo benefício';
  const image = highlight.dataset.image ? `<img src="${escapeHtml(highlight.dataset.image)}" alt="">` : '';
  const choice = await openSheet({
    html: `<h2 class="narte-cart-layer__title">Falta só ${money(missing)} para <span class="narte-cart__hl">${escapeHtml(prize)}</span></h2>
      <p class="narte-cart-layer__text">${escapeHtml(highlight.dataset.sub || 'Este produto completa o valor:')}</p>
      <div class="narte-cart-layer__item">${image}<div><strong>${escapeHtml(highlight.dataset.title)}</strong><span>${money(highlight.dataset.price)}</span></div></div>`,
    yes: 'Adicionar e ganhar',
    no: 'Continuar sem o benefício',
  });
  if (choice === true) {
    await post('cart/add.js', { items: [{ id: Number(highlight.dataset.narteExtra), quantity: 1 }] });
    return;
  }
  if (choice === false) {
    const button = trigger.isConnected
      ? trigger
      : /** @type {HTMLElement|null} */ (document.querySelector('.cart-drawer__dialog .narte-cart__bar [name="checkout"]'));
    if (button) {
      button.dataset.narteNear = 'done';
      button.click();
    }
  }
}

if (!window.__narteCart) {
  window.__narteCart = true;
  document.addEventListener('pointerdown', (e) => {
    if (/** @type {HTMLElement} */ (e.target).closest?.('.cart-drawer__dialog')) state.lastDrawerTouch = Date.now();
  }, true);
  // Na janela, em captura: roda antes do guarda de brindes (tiered-rewards-bar), que também segura o "Finalizar".
  window.addEventListener('click', onCheckout, true);
  document.addEventListener('click', onClick);
  document.addEventListener('cart:update', scheduleSnapshot);
  document.addEventListener('cart:change', scheduleSnapshot);
  // O carrinho lateral fecha: tira avisos que ficaram abertos.
  document.addEventListener('close', (e) => {
    if (e.target === drawerDialog()) {
      layer().replaceChildren();
    }
  }, true);
  fetchCart().then((cart) => (state.cart = cart)).catch(() => {});
}
